import React from 'react';

import styled from 'styled-components';

import { useCommunityInfoContext } from 'components/CommunityInfo/CommunityInfoContext';
import { isNonNegativeNumber } from 'utils/helpers/numbers';
import { formatAreaByUnitSystem, getAreaUnitLabel } from 'utils/helpers/units';

import { LivingInfoDataType } from '../types';
import { calculateLivingInfoStats, formatNumberWithCommas } from '../utils';

const Container = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 50px;
  padding-right: 15px;
  padding-top: 8px;
  position: relative;
  &::after {
    content: '';
    height: 2px;
    width: calc(100% - 15px);
    border-radius: 1px;
    background-image: linear-gradient(90deg, #0ab776, #fddf05, #ff6400);
    position: absolute;
    top: 0px;
  }
`;

const Column = styled.div`
  font-family: var(--font-regular);
  font-size: 10px;
  color: var(--text-secondary);
  width: 60px;
  &:last-child {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
`;

const Price = styled.div`
  font-family: var(--font-regular);
  font-size: 10px;
  color: var(--text-primary);
`;

const TextGray = styled.span`
  font-family: var(--font-regular);
  font-size: 10px;
  color: var(--text-secondary);
`;

type Props = {
  data: LivingInfoDataType[];
};

const AccommodationGroupCollapseHeaderStats = ({ data }: Props) => {
  const { unitSource } = useCommunityInfoContext();
  const { minBaseRentItem, avgBaseRent, maxBaseRentItem, minSqFootage, avgSqFootage, maxSqFootage } =
    calculateLivingInfoStats(data);
  const areaUnitLabel = getAreaUnitLabel(unitSource);
  const aggregatorLabels = ['min', 'avg', 'max'];
  const stats = [
    { base_rent: minBaseRentItem?.base_rent, sq_footage: minSqFootage },
    { base_rent: avgBaseRent, sq_footage: avgSqFootage },
    { base_rent: maxBaseRentItem?.base_rent, sq_footage: maxSqFootage },
  ];

  return (
    <Container data-test-id={`cd-accommodation-room-type-header-stats`}>
      {stats.map((item, idx) => (
        <Column key={idx}>
          {isNonNegativeNumber(item?.base_rent) && (
            <Price data-test-id={`cd-accommodation-room-type-header-stats-${aggregatorLabels[idx]}-value`}>
              ${formatNumberWithCommas(Number(item?.base_rent ?? 0))} <TextGray>{aggregatorLabels[idx]}</TextGray>
            </Price>
          )}
          {typeof item?.sq_footage === 'number' && item.sq_footage > 1 && (
            <div data-test-id={`cd-accommodation-room-type-header-stats-${aggregatorLabels[idx]}-area`}>
              {formatAreaByUnitSystem(item.sq_footage, unitSource)} {areaUnitLabel}
            </div>
          )}
        </Column>
      ))}
    </Container>
  );
};

export default AccommodationGroupCollapseHeaderStats;
